/**
 * Recharts theme from Tide tokens: axis, grid and tooltip, plus a motion hook.
 *
 * Charts animate a state change for the "local" role (220ms) and do not
 * animate at all when the reader asks for reduced motion. Where matchMedia is
 * unavailable (jsdom, very old browsers) motion is treated as reduced, so
 * nothing waits on an animation that cannot be observed.
 */
import { useEffect, useState } from 'react';

export const CHART_MOTION_MS = 220;

export const tideAxisTick = { fill: 'var(--tide-ink-2)', fontSize: 12 };
export const tideAxisLine = { stroke: 'var(--tide-line)' };
export const tideGrid = { stroke: 'var(--tide-line)', strokeDasharray: '2 4' };
export const tideTooltip = {
  contentStyle: {
    background: 'var(--tide-surface)',
    border: '1px solid var(--tide-boundary)',
    borderRadius: 6,
    fontSize: 12,
    color: 'var(--tide-ink)',
    boxShadow: 'var(--tide-elev-2)',
  },
  labelStyle: { color: 'var(--tide-ink)', fontWeight: 600 },
  itemStyle: { color: 'var(--tide-ink)' },
  cursor: { fill: 'var(--tide-interact-soft)', fillOpacity: 0.6 },
};
/** Opacity for a series a legend has dimmed (isolate mode). */
export const DIMMED_OPACITY = 0.16;

const QUERY = '(prefers-reduced-motion: reduce)';

export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  try {
    return window.matchMedia(QUERY).matches;
  } catch {
    return true;
  }
}

/** Live reduced-motion preference: follows the OS setting while the page is open. */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(prefersReducedMotion);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    let mql: MediaQueryList;
    try { mql = window.matchMedia(QUERY); } catch { return undefined; }
    const onChange = () => setReduced(mql.matches);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);
  return reduced;
}

/** Spread onto a recharts series: `<Area {...motion} />`. */
export function useChartMotion(): { isAnimationActive: boolean; animationDuration: number; animationEasing: 'ease-out' } {
  const reduced = useReducedMotion();
  return { isAnimationActive: !reduced, animationDuration: CHART_MOTION_MS, animationEasing: 'ease-out' };
}
